import * as React from "react";
import { createPortal } from "react-dom";
import { ConfirmationCard, type ConfirmationCardConfig } from "./confirmation-card.js";

/**
 * The app's confirmation step for anything that hits the API: a modal
 * ConfirmationCard stating, in one short line, what the action will actually
 * do -- in place of the browser's native confirm().
 *
 * Buttons never call this directly: `<AsyncButton confirm={{ ... }}>` opens it
 * and only starts its own pending animation once the user confirms. Use the
 * `useConfirm()` hook only for an action that isn't an AsyncButton.
 */
export type ConfirmTone = "default" | "success" | "info" | "warning" | "destructive";

export interface ConfirmRequest {
  /** The question, e.g. "Lock compensation?" */
  title: React.ReactNode;
  /** One short line: what this actually does once confirmed. */
  description?: React.ReactNode;
  /** Label of the confirming action. */
  actionText?: React.ReactNode;
  cancelText?: React.ReactNode;
  /** Colors. Mirror the trigger button's variant. */
  tone?: ConfirmTone;
  /** Escape hatch: merged over the tone preset (see ConfirmationCardConfig). */
  config?: ConfirmationCardConfig;
}

/** Card config shared by every tone -- a dialog is shorter and tighter than a
 * standalone success card, so it overrides confirmationCardDefaults. */
export const confirmDialogDefaults = {
  width: 420,
  minHeight: 0,
  padding: 26,
  borderRadius: 22,
  titleFontSize: 21,
  // No icon above it, so the title starts at the card's own padding.
  titleMarginTop: 0,
  titleMarginBottom: 10,
  descriptionFontSize: 13.5,
  descriptionLineHeight: 21,
  buttonMarginTop: 20,
  animationDuration: 700,
} as const satisfies ConfirmationCardConfig;

/** Only the colors differ per tone; "success" is the card's own default.
 * Each tone is the muted single tone (--color-error / -warning / -info,
 * brand-400) mixed into the card's own surface, so hovering washes the card
 * in a faint tint of it rather than swapping to a saturated alert block. The
 * mix is against --color-surface-0, so it follows light and dark mode. */
const tone = (color: string): ConfirmationCardConfig => ({
  accentColor: `color-mix(in srgb, ${color} 35%, var(--color-surface-0))`,
  hoverBackgroundColor: `color-mix(in srgb, ${color} 14%, var(--color-surface-0))`,
});

const toneConfig: Record<ConfirmTone, ConfirmationCardConfig> = {
  default: { buttonColor: "var(--color-brand-700)", ...tone("var(--color-brand-400)") },
  success: {},
  info: { buttonColor: "var(--color-info-fg)", ...tone("var(--color-info)") },
  warning: { buttonColor: "var(--color-warning-fg)", ...tone("var(--color-warning)") },
  destructive: { buttonColor: "var(--color-error-fg)", ...tone("var(--color-error)") },
};

export type ConfirmFn = (request: ConfirmRequest) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

/** Without the provider mounted the action still asks -- just natively. */
const nativeFallback: ConfirmFn = (request) => {
  const text = [request.title, request.description]
    .filter((part): part is string => typeof part === "string")
    .join("\n\n");
  return Promise.resolve(window.confirm(text || "Are you sure?"));
};

/** Resolves true when the user confirms, false on Cancel / Esc / backdrop. */
export function useConfirm(): ConfirmFn {
  return React.useContext(ConfirmContext) ?? nativeFallback;
}

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [request, setRequest] = React.useState<ConfirmRequest | null>(null);
  const resolveRef = React.useRef<((ok: boolean) => void) | null>(null);
  const restoreFocusRef = React.useRef<HTMLElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  const settle = React.useCallback((ok: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setRequest(null);
    restoreFocusRef.current?.focus?.();
    restoreFocusRef.current = null;
    resolve?.(ok);
  }, []);

  const confirm = React.useCallback<ConfirmFn>(
    (next) =>
      new Promise<boolean>((resolve) => {
        // A second request while one is open cancels the first rather than
        // leaving its promise hanging forever.
        resolveRef.current?.(false);
        resolveRef.current = resolve;
        restoreFocusRef.current = document.activeElement as HTMLElement | null;
        setRequest(next);
      }),
    []
  );

  // Focus the panel itself, not the confirm action: Esc and Tab work, but a
  // stray Enter can't fire a destructive action the user hasn't read yet.
  React.useEffect(() => {
    if (request) panelRef.current?.focus();
  }, [request]);

  React.useEffect(() => {
    if (!request) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        settle(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [request, settle]);

  const tone = request?.tone ?? "default";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request &&
        createPortal(
          <div
            // pointer-events-auto: a Radix modal Dialog sets `pointer-events:
            // none` on <body>, and this portal is a body sibling of that
            // dialog -- without re-enabling it here, a confirm opened from
            // inside a modal (its Confirm/Cancel buttons) receives no clicks.
            // This layer sits above the dialog (z-[10000] > the dialog's z-100).
            className="pointer-events-auto fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[2px]"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) settle(false);
            }}
          >
            <div
              ref={panelRef}
              role="alertdialog"
              aria-modal="true"
              aria-label={typeof request.title === "string" ? request.title : "Confirm action"}
              tabIndex={-1}
              className="w-full max-w-[420px] outline-none"
              onKeyDown={(e) => {
                // Enter confirms only from the panel itself (never while a
                // child button has focus, which handles its own Enter), and
                // never for a destructive action.
                if (e.key === "Enter" && e.target === e.currentTarget && tone !== "destructive") {
                  e.preventDefault();
                  settle(true);
                }
              }}
            >
              <ConfirmationCard
                content={{
                  title: request.title,
                  description: request.description,
                  actionText: request.actionText ?? "Confirm",
                  cancelText: request.cancelText ?? "Cancel",
                }}
                config={{ ...confirmDialogDefaults, ...toneConfig[tone], ...request.config }}
                onAction={() => settle(true)}
                onCancel={() => settle(false)}
              />
            </div>
          </div>,
          document.body
        )}
    </ConfirmContext.Provider>
  );
}
